'use client';

import { Ref, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import getIcon, { IconsSize } from '../icons';
import useCommonProps, { BaseComponent } from '../../hooks/useCommonProps';
import { SvgComponent, SvgType } from '../svg/svg.component';
import style from './tailfin-hero-banner.component.module.less';
import {
  ButtonProps,
  ButtonPriority,
  ButtonComponent,
  ButtonSize,
} from '@vaad1st/component.atom.button';
import {
  TypographyComponent,
  TypographyVariant,
  TypographySize,
  TypographyWeight,
} from '@vaad1st/component.atom.typography';
import { ButtonSkin } from '../tailfin-hero-banner-two/tailfin-hero-banner-two.component';
import { HeadingElement } from 'src/utils/shared-types';

export enum TailfinHeroBannerPropsThemes {
  Light = 'light',
  Dark = 'dark',
}

export interface TailfinHeroBannerProps extends BaseComponent {
  heading: string;
  headingTag?: HeadingElement;
  description: string;
  image: {
    url: string;
    alt: string;
  };
  buttons: any[];
  theme?: TailfinHeroBannerPropsThemes;
  motion?: boolean;
}

export const TailfinHeroBannerComponent = ({
  heading,
  headingTag = HeadingElement.H1,
  description,
  image,
  buttons,
  theme = TailfinHeroBannerPropsThemes.Light,
  motion = true,
  testId = 'tailfin-hero-banner-component',
  testAttribute,
  testAttributeValue,
}: TailfinHeroBannerProps) => {
  const containerEl: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState<boolean>(false);
  const [timing, setTiming] = useState<boolean>(true);
  const [scrollTriggerPosition, setScrollTriggerPosition] = useState<number>(0);
  const motionPreferenceRef = useRef<boolean | null>(null);

  const commonProps = useCommonProps({
    testId,
    testAttribute,
    testAttributeValue: testAttributeValue || testId,
  });

  const isDarkTheme = theme === TailfinHeroBannerPropsThemes.Dark;

  const config = useMemo(
    () => ({
      animateTimeout: 5000,
      scrollThreshold: 10,
      shouldAnimate: motionPreferenceRef.current ?? motion,
    }),
    [motion, motionPreferenceRef]
  );

  useEffect(() => {
    motionPreferenceRef.current = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const el = containerEl.current as HTMLDivElement;
    let timerFunc: string | number | NodeJS.Timeout | undefined;

    const scrollingFunc = () => {
      if (getScrollPosition() >= scrollTriggerPosition) {
        setToAnimated();
      }
    };

    const intersectionListener = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      const fullRatio = entry?.intersectionRatio ?? 0;
      const ratio = Number(fullRatio.toFixed(2));

      if (ratio >= 0.8) {
        timerFunc = setTimeout(() => {
          setToAnimated();
        }, config.animateTimeout);
      }

      if (ratio >= 0.9) {
        setScrollTriggerPosition(getScrollPosition() + config.scrollThreshold);
        observer.unobserve(el);
        window.addEventListener('scroll', scrollingFunc);
      }
    };

    const setToAnimated = () => {
      setAnimated(true);
      setTiming(false);
      clearTimeout(timerFunc);
      window.removeEventListener('scroll', scrollingFunc);
    };

    if (!window || !window.IntersectionObserver || !motion) {
      setToAnimated();
      return;
    }

    const getScrollPosition = () =>
      document.documentElement.scrollTop || document.body.scrollTop;

    const observer = new IntersectionObserver(intersectionListener, {
      root: null,
      rootMargin: '0px',
      threshold: [0.8, 0.9],
    });

    observer.observe(el);

    return () => {
      observer.unobserve(el);
      window.removeEventListener('scroll', scrollingFunc);
    };
  }, [config, motion, timing, containerEl, scrollTriggerPosition]);

  return (
    <div className={style.root} ref={containerEl} {...commonProps}>
      <article
        className={classNames(style.content, {
          [style['content--dark']]: isDarkTheme,
          [style['content--animated']]: animated,
          [style['content--static']]: !config.shouldAnimate,
        })}
      >
        <div className={style['content-inner']}>
          <div className={style.title}>
            <TypographyComponent
              element={headingTag}
              variant={TypographyVariant.Heading}
              size={TypographySize.Lrg}
              weight={TypographyWeight.Regular}
            >
              {heading}
            </TypographyComponent>
          </div>
          <div className={style.subtitle}>
            <TypographyComponent
              element="h2"
              variant={TypographyVariant.Body}
              size={TypographySize.Mdm}
              weight={TypographyWeight.Regular}
            >
              {description}
            </TypographyComponent>
          </div>
          <div className={style.buttons}>
            {buttons.map((props, i) => {
              const priority =
                i === 0 ? ButtonPriority.Primary : ButtonPriority.Secondary;
              let skin =
                i === 0 || animated ? ButtonSkin.Default : ButtonSkin.Inverted;
              let iconChild = null;

              if (props?.icon) {
                const C = getIcon(props?.icon);
                iconChild = <C />;
              }

              if (isDarkTheme && i !== 0 && !animated) {
                skin = ButtonSkin.Neutral;
              }

              return (
                <div className={style.button} key={i}>
                  <ButtonComponent
                    size={ButtonSize.Medium}
                    {...{ priority, skin, iconChild, ...props }}
                    fullWidth={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </article>

      <div
        className={classNames(style.image, {
          [style['image--animated']]: animated,
          [style['image--static']]: !config.shouldAnimate,
        })}
        style={{
          backgroundImage: `url('${image.url}')`,
        }}
      >
        <span role="img" aria-label={image.alt} className="image-alt" />
      </div>

      <div
        className={classNames(style['tailfin-wrap'], {
          [style['tailfin-wrap--animated']]: animated,
          [style['tailfin-wrap--static']]: !config.shouldAnimate,
        })}
      >
        <SvgComponent
          type={SvgType.ExtendedTailFin}
          className={classNames(style.tailfin, style['tailfin--front'])}
          preserveAspectRatio="xMinYMin meet"
        />
      </div>
      <div
        className={classNames(style['tailfin-wrap'], {
          [style['tailfin-wrap--animated']]: animated,
          [style['tailfin-wrap--static']]: !config.shouldAnimate,
        })}
      >
        <SvgComponent
          type={SvgType.ExtendedTailFinTall}
          className={classNames(style.tailfin, style['tailfin--rear'])}
          preserveAspectRatio="xMinYMin meet"
        />
      </div>
    </div>
  );
};
